import {
    clone,
    createEmployeePayrollData,
    CreateOrUpdateDocumentParams,
    createTableRow,
    type DocumentDTO,
    type Employee,
    Feature,
    fieldValueChanged,
    fileExtension,
    fileIcon,
    type Form,
    FORM_CONFIRMATION_TEXTS,
    formatDate,
    type FormField,
    FormStatus,
    formStatusColor,
    formStatusLabel,
    GetEmployeePayrollDataParams,
    getRenderedFields,
    INVALID_IBAN_MESSAGE,
    isPendingInvite,
    isValidIban,
    mapFormFieldsToObject,
    MimeType,
    UpdateEmployeePayrollDataParams,
} from "@pentacode/core";
import "@pentacode/ui/components";
import DOMPurify from "dompurify";
import { css, html, nothing, type TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { alert, confirm } from "../../elements/alert-dialog";
import { Dialog } from "../../elements/dialog";
import "../../elements/popover";
import "../../elements/scroller";
import { app } from "../../init";
import { singleton } from "../../lib/singleton";
import { downloadFromUrl } from "../../lib/util";
import { mixins, shared } from "../../styles";
import "./edit-document-dialog";
import type { EditDocumentDialog } from "./edit-document-dialog";
import "./signature-dialog";
import type { SignatureDialog } from "./signature-dialog";

@customElement("ptc-document-dialog")
export class DocumentDialog extends Dialog<DocumentDTO, boolean> {
    readonly preventDismiss = true;

    @state()
    private _document: DocumentDTO;

    @state()
    private _form: Form | null = null;

    @state()
    private _edited = false;

    @singleton("ptc-edit-document-dialog")
    private _editDocumentDialog: EditDocumentDialog;

    @query("form")
    private _formElement: HTMLFormElement;

    @singleton("ptc-signature-dialog")
    private _signatureDialog: SignatureDialog;

    @state()
    private _expandedTableSections = new Set<string>();

    @state()
    private _showActions = false;

    private get _employee() {
        return app.getEmployee(this._document.employeeId);
    }

    show(document: DocumentDTO, opts: { showActions?: boolean } = {}) {
        this._document = document;
        this._form = this._document.form && clone(this._document.form);
        this._edited = false;
        this._showActions = opts.showActions ?? false;
        // pdfjs-dist (~1.5MB) is only needed to render PDFs. Pull it in on demand;
        // the <ptc-pdf-viewer> tag upgrades once the chunk registers it.
        if (document.type === MimeType.PDF) {
            void import("./pdf-viewer");
        }
        return super.show();
    }

    private async _edit() {
        this.hide();
        const edited = await this._editDocumentDialog.show({ document: this._document });
        if (edited) {
            this._edited = true;
            this._document = edited;
        }
        this._show();
    }

    private async _delete() {
        this.hide();

        const confirmed = await confirm(
            "Sind Sie sicher dass Sie dieses Dokument löschen wollen?",
            "Löschen",
            "Abbrechen",
            { title: "Dokument Löschen", type: "destructive" }
        );

        if (confirmed && this._document.id) {
            await app.api.deleteDocument(this._document.id);
            this.done(true);
        } else {
            this._show();
        }
    }

    private async _download() {
        if (!this._document.url) {
            return;
        }
        await downloadFromUrl(this._document.url, `${this._document.name}.${fileExtension(this._document.type)}`);
    }

    private async _saveForm() {
        if (!this._form) {
            return;
        }

        this.loading = true;

        try {
            this._document = await app.api.createOrUpdateDocument(
                new CreateOrUpdateDocumentParams({ ...this._document, form: this._form })
            );
            this._edited = true;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._form = this._document.form && clone(this._document.form);

        this.loading = false;
    }

    private async _submitForm(e?: Event) {
        e?.preventDefault();
    }

    private async _finalizeOrShareForm() {
        const choice = await alert(
            "Möchten Sie dieses Formular dem Mitarbeiter zum Bearbeiten freigeben oder direkt in ein Dokument umwandeln?",
            {
                title: "Formular Absenden",
                icon: "icon.action.publish",
                optionsLayout: "vertical",
                options: [
                    {
                        label: html`<ptc-ui-icon name="icon.action.publish"></ptc-ui-icon> Zum Bearbeiten Freigeben`,
                        buttonClass: "transparent box",
                    },
                    {
                        label: html`<ptc-ui-icon name="icon.action.finalize"></ptc-ui-icon> Fertigstellen`,
                        buttonClass: "transparent box",
                    },
                ],
            }
        );

        switch (choice) {
            case 0:
                await this._shareForm();
                break;
            case 1:
                await this._finalizeForm();
                break;
        }
    }

    private async _shareForm() {
        if (!this._form || !this._formElement.checkValidity()) {
            await this._expandSectionsWithInvalidFields();
            return;
        }

        const employee = this._employee;
        if (!employee || (!employee.accountId && !employee.email)) {
            this.hide();
            await alert(
                "Das Formular kann erst freigegeben werden, wenn für den Mitarbeiter eine Email-Adresse hinterlegt ist. Bitte ergänzen Sie diese zunächst in den Stammdaten des Mitarbeiters.",
                { title: "Email-Adresse Erforderlich", type: "warning" }
            );
            this._show();
            return;
        }

        this.loading = true;

        try {
            this._form.status = FormStatus.InProgress;
            this._document = await app.api.createOrUpdateDocument(
                new CreateOrUpdateDocumentParams({ ...this._document, form: this._form })
            );
            void alert(this._formSharedMessage(employee), { title: "Formular Freigegeben", type: "success" });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._form = this._document.form && clone(this._document.form);
        this._edited = true;

        this.loading = false;
    }

    private _formSharedMessage(employee: Employee) {
        if (employee.accountId) {
            return "Das Formular wurde mit dem Mitarbeiter geteilt und kann nun über die Mitarbeiter-App ausgefüllt werden. Der Mitarbeiter wurde per Email informiert.";
        }

        if (isPendingInvite(employee.invite)) {
            return "Das Formular wurde mit dem Mitarbeiter geteilt und der Mitarbeiter per Email informiert. Sobald er seinen Zugang über die Einladung eingerichtet hat, kann er das Formular in der Mitarbeiter-App ausfüllen.";
        }

        return html`Das Formular wurde mit dem Mitarbeiter geteilt und der Mitarbeiter per Email informiert. Ausfüllen
            kann er es allerdings erst mit Zugang zur Mitarbeiter-App. Die Einladung können Sie unter
            <strong>Zugänge & Rechte</strong> versenden.`;
    }

    private async _completeForm() {
        if (!this._form || !this._formElement.checkValidity()) {
            await this._expandSectionsWithInvalidFields();
            return;
        }

        this.loading = true;

        try {
            this._form.status = FormStatus.Completed;
            this._document = await app.api.createOrUpdateDocument(
                new CreateOrUpdateDocumentParams({ ...this._document, form: this._form })
            );
            this.done(true);
            void alert("Das ausgefüllte Formular wurde erfolgreich übermittelt.", {
                title: "Formular Übermittelt",
                type: "success",
            });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._form = this._document.form && clone(this._document.form);
        this.loading = false;
    }

    private async _finalizeForm() {
        const form = this._form;
        const employee = this._employee;
        if (!employee || !form || !this._formElement.checkValidity()) {
            await this._expandSectionsWithInvalidFields();
            return;
        }

        this.loading = true;

        const updatesFromEmployeeFields = mapFormFieldsToObject(form.fields, "employee.");

        const changedEmployeeFields = getRenderedFields(form).filter((field) =>
            fieldValueChanged(field, employee, "employee.")
        );

        const updateEmployee =
            changedEmployeeFields.length &&
            (await confirm(
                html`
                    <div>Möchten Sie die folgenden Angaben in die Stammdaten des Mitarbeiters übernehmen?</div>
                    <table
                        class="vertically-margined"
                        style="border-collapse: separate; border-spacing: 0.5em; margin-bottom: 1em;"
                    >
                        ${changedEmployeeFields.map(
                            (field) => html`
                                <tr>
                                    <th class="bold margined">${field.label}</th>
                                    <td class="margined">${form!.getHumanReadableValue(field)}</td>
                                </tr>
                            `
                        )}
                    </table>
                `,
                "Ja",
                "Nein",
                { title: "Angaben Übernehmen", icon: "icon.object.form", optionsLayout: "horizontal" }
            ));

        try {
            if (updateEmployee) {
                await app.updateEmployee(employee, updatesFromEmployeeFields);
            }

            const employeePayrollData = await app.api.getEmployeePayrollData(
                new GetEmployeePayrollDataParams({ employeeId: employee.id })
            );

            const updatesFromPayrollFields = new UpdateEmployeePayrollDataParams({
                employeeId: employee.id,
                ...mapFormFieldsToObject(form.fields, "employeePayrollData."),
            });

            if (updatesFromPayrollFields.bankDetails?.iban && !isValidIban(updatesFromPayrollFields.bankDetails.iban)) {
                await alert(INVALID_IBAN_MESSAGE, { type: "warning" });
                return;
            }

            const changedPayrollFields = getRenderedFields(form).filter((field) =>
                fieldValueChanged(field, employeePayrollData, "employeePayrollData.")
            );

            if (app.company.features.includes(Feature.PentacodePayroll)) {
                // @TODO: remove feature flag
                const updatePayrollData =
                    changedPayrollFields.length &&
                    (await confirm(
                        html` <div>Möchten Sie die Lohndaten des Mitarbeiters übernehmen?</div> `,
                        "Ja",
                        "Nein",
                        { title: "Angaben Übernehmen", icon: "icon.object.form", optionsLayout: "horizontal" }
                    ));
                if (updatePayrollData) {
                    await app.api.updateEmployeePayrollData(
                        createEmployeePayrollData({ ...updatesFromPayrollFields, employeeId: employee.id })
                    );
                }
            } else {
                // @TODO: remove this when removing feature flag
                // save payroll data without confirmation
                await app.api.updateEmployeePayrollData(
                    createEmployeePayrollData({ ...updatesFromPayrollFields, employeeId: employee.id })
                );
            }

            form.status = FormStatus.Finalized;
            this._document = await app.api.createOrUpdateDocument(
                new CreateOrUpdateDocumentParams({ ...this._document, form })
            );
            this._edited = true;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._form = this._document.form && clone(this._document.form);

        this.loading = false;
    }

    private async _editManagerSignature() {
        if (!this._form) {
            return;
        }

        this._signatureDialog.requestContactInfo = false;

        const result = await this._signatureDialog.show();
        if (result) {
            this._form.managerSignature = {
                employeeId: app.profile!.id,
                name: app.profile!.firstName + " " + app.profile!.lastName,
                src: result.src,
                time: new Date().toISOString(),
            };
            this.requestUpdate();
        }

        this._show();
    }

    private async _editEmployeeSignature() {
        if (!this._form) {
            return;
        }

        this._signatureDialog.requestContactInfo = false;

        const result = await this._signatureDialog.show();
        if (result) {
            this._form.employeeSignature = {
                employeeId: app.profile!.id,
                name: app.profile!.firstName + " " + app.profile!.lastName,
                src: result.src,
                time: new Date().toISOString(),
            };
            this.requestUpdate();
        }

        this._show();
    }

    private async _editCaretakerSignature() {
        if (!this._form) {
            return;
        }

        this._signatureDialog.requestContactInfo = true;

        const result = await this._signatureDialog.show();
        if (result) {
            this._form.caretakerSignature = {
                name: result.info?.firstName + " " + result.info?.lastName,
                src: result.src,
                info: result.info,
                time: new Date().toISOString(),
            };
            this.requestUpdate();
        }

        this._show();
    }

    private async _expandSectionsWithInvalidFields() {
        const allInputs = this._formElement.querySelectorAll("input, select");

        //find collapses of all invalid input fields and expand them
        const invalidCollapsedSections = Array.from(allInputs)
            .filter((input) => input.id.split("_").length === 5)
            .filter((input) => !(input as HTMLInputElement).checkValidity())
            .map((input) => {
                const [, sectionId, fieldId, rowId] = input.id.split("_");
                return `field_${sectionId}_${fieldId}_${rowId}`;
            })
            .filter((id) => !this._expandedTableSections.has(id));

        invalidCollapsedSections.forEach((id) => this._expandedTableSections.add(id));

        this.requestUpdate();
        await this.updateComplete;

        if (invalidCollapsedSections.length) {
            /* the collapse takes some time to expand. If we immmediately report validity, 
            the popover position will be wrong */
            setTimeout(() => {
                this._formElement?.reportValidity();
            }, 100);
        } else {
            this._formElement?.reportValidity();
        }
    }

    private _toggleRequiredConfirmation(e: Event, confirmationKey: keyof NonNullable<Form["requiredConfirmations"]>) {
        if (!this._form) {
            return;
        }

        const checked = (e.target as HTMLInputElement).checked;

        //initialize requiredConfirmations if not present
        if (!this._form.requiredConfirmations) {
            this._form.requiredConfirmations = {
                dataProtection: { checked: false, time: "" },
                idCard: { checked: false, time: "" },
                updateInformation: { checked: false, time: "" },
            };
        }

        this._form.requiredConfirmations[confirmationKey] = { checked, time: new Date().toISOString() };

        this.requestUpdate();
    }

    static styles = [
        shared,
        ...Dialog.styles,
        css`
            .inner {
                background: none;
                box-shadow: none;
                border-radius: 0;
                ${mixins.fullbleed()};
                max-width: none;
                container: document-dialog / inline-size;
            }

            :host([open]) .scrim {
                opacity: 1;
            }

            form,
            .form-actions {
                max-width: 40em;
                width: 100%;
                margin: 0px auto;
                padding: 0.5em;
                box-sizing: border-box;
            }

            select[data-readonly="true"] {
                pointer-events: none;
            }

            .form-title {
                margin: 1em 0 0.5em 0;
            }

            .section {
                margin: 2em 0;
            }

            .section h2 {
                margin: 1em 0 0.5em 0;
            }

            .section-description {
                margin: 0 0 0.5em 0;
            }

            .section-fields {
                display: grid;
                grid-gap: 1em;
                grid-template-columns: minmax(10em, 1fr) minmax(20em, 1fr);
                align-items: center;
            }

            input[type="checkbox"] {
                justify-self: start;
            }

            label {
                padding: 0 0.5em;
            }

            .signature {
                width: 10em;
                height: 5em;
            }

            input[type="date"] {
                text-align: left;
            }

            table {
                width: 100%;
                border-collapse: collapse;
                font-size: var(--font-size-smaller);
            }

            th,
            td {
                padding: 0.5em;
            }

            th {
                font-weight: 600;
            }

            tbody tr {
                border-top: solid 1px var(--shade-2);
            }

            @container document-dialog (width < 33em) {
                .section-fields {
                    display: flex;
                    flex-direction: column;
                    align-items: stretch;
                    grid-gap: 0;
                }

                label {
                    padding: 0.5em;
                    margin-top: 0.5em;
                }

                input[type="checkbox"] {
                    align-self: flex-end;
                    margin-top: -1.7em;
                    margin-right: 1em;
                }

                .signatures {
                    flex-direction: column !important;
                    clear: both;
                }

                h1.horizontal.layout {
                    flex-direction: column;
                    gap: 0.3em;
                    align-items: start;
                }
            }

            table td > input {
                width: 100%;
            }
        `,
    ];

    private _renderPreview() {
        if (!this._document?.url) {
            return null;
        }

        switch (this._document.type) {
            case MimeType.JPG:
            case MimeType.PNG:
                return html`<div class="fullbleed vertical layout center-aligning">
                    <ptc-scroller class="stretch double-margined center-justifying">
                        <img src="${this._document.url}" style="width: 100%; object-fit: scale-down" />
                    </ptc-scroller>
                </div>`;
            case MimeType.CSV:
            case MimeType.TXT:
                return html`
                    <iframe
                        src="https://docs.google.com/gview?embedded=true&amp;url=${encodeURIComponent(
                            this._document.url
                        )}"
                        width="100%"
                        height="100%"
                    >
                    </iframe>
                `;
            case MimeType.MSExcel:
            case MimeType.MSExcelLegacy:
            case MimeType.MSWord:
            case MimeType.MSWordLegacy:
                return html`
                    <iframe
                        src="https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                            this._document.url
                        )}"
                        width="100%"
                        height="100%"
                    >
                        This is an embedded <a target="_blank" href="http://office.com">Microsoft Office</a> document,
                        powered by <a target="_blank" href="http://office.com/webapps">Office Online</a>.
                    </iframe>
                `;
            case MimeType.PDF:
                return html` <ptc-pdf-viewer class="fullbleed" .src=${this._document.url}></ptc-pdf-viewer> `;
            default:
                return html`
                    <object
                        style="width: 100%; height: 100%;"
                        .type=${this._document.type}
                        .data=${`${this._document.url}#pagemode=0&toolbar=0&statusbar=0&navpanes=0&scrollbar=0&messages=0`}
                    ></object>
                `;
        }
    }

    private _renderField(
        field: FormField,
        sectionIndex: number,
        fieldIndex: number,
        subIndex: string | undefined = undefined,
        readonly = false,
        renderLabel = true
    ): TemplateResult<1> {
        const form = this._form!;
        if (field.condition && !form.checkCondition(field.condition)) {
            return html``;
        }
        const fieldName = `field_${sectionIndex}_${fieldIndex}${subIndex ? `_${subIndex}` : ""}`;
        if (form.status === FormStatus.InProgress && field.managerOnly) {
            readonly = true;
        }
        const required =
            !!field.required &&
            ((field.managerOnly && form.status === FormStatus.Draft) || this._form!.status === FormStatus.InProgress);
        return html`
            ${renderLabel && (field.label || field.description)
                ? html`<label for="${fieldName}">
                      ${field.label} ${required ? html`<span class="red colored-text">*</span>` : ""}
                      ${field.description ? html`<div class="small subtle">${field.description}</div>` : ""}
                  </label>`
                : ""}
            ${this._renderFieldInput(field, fieldName, required, readonly, sectionIndex, fieldIndex)}
        `;
    }

    private _renderFieldInput(
        field: FormField,
        fieldName: string,
        required: boolean,
        readonly: boolean,
        sectionIndex: number,
        fieldIndex: number
    ): TemplateResult<1> {
        switch (field.type) {
            case "boolean":
                return html`
                    <input
                        type="checkbox"
                        id="${fieldName}"
                        name="${fieldName}"
                        .checked=${Boolean(field.value)}
                        .readOnly=${readonly}
                        ?required=${required}
                        @change=${(e: Event) => {
                            field.value = (e.target as HTMLInputElement).checked;
                            this.requestUpdate();
                        }}
                        style="justify-self: start"
                    />
                `;

            case "table":
                if (field.data && field.headers) {
                    return html`${this._renderTableData(field, sectionIndex, fieldIndex, readonly)}`;
                }
                return html``;

            case "date":
                return html`
                    <input
                        type="date"
                        id="${fieldName}"
                        name="${fieldName}"
                        .value=${field.value?.toString() || ""}
                        ?required=${required}
                        @change=${(e: Event) => {
                            field.value = (e.target as HTMLInputElement).value;
                            this.requestUpdate();
                        }}
                        .readOnly=${readonly}
                        .pattern=${field.pattern || `.*`}
                    />
                `;

            case "number":
                if (field.options) {
                    return html`
                        <select
                            id="${fieldName}"
                            name="${fieldName}"
                            .value=${field.value?.toString() || ""}
                            @change=${(e: Event) => {
                                const val = (e.target as HTMLSelectElement).value;
                                field.value = Number(val);
                                this.requestUpdate();
                            }}
                            ?required=${required}
                            data-readonly="${readonly}"
                        >
                            <option value="">-- Bitte Wählen --</option>
                            ${field.options.map(
                                (option) => html`
                                    <option .value=${option.value.toString()} ?selected=${option.value === field.value}>
                                        ${option.label}
                                    </option>
                                `
                            )}
                        </select>
                    `;
                }
                return html`
                    <input
                        type="number"
                        id="${fieldName}"
                        name="${fieldName}"
                        .value=${field.value?.toString() || ""}
                        ?required=${required}
                        @change=${(e: Event) => {
                            const value = (e.target as HTMLInputElement).value;
                            field.value = value ? Number(value) : undefined;
                            this.requestUpdate();
                        }}
                        .readOnly=${readonly}
                        .min=${field.min}
                        .max=${field.max}
                        .step=${field.step || "1"}
                    />
                `;

            default:
                if (field.options) {
                    return html`
                        <select
                            id="${fieldName}"
                            name="${fieldName}"
                            .value=${field.value?.toString() || ""}
                            @change=${(e: Event) => {
                                const val = (e.target as HTMLSelectElement).value;
                                field.value = val;
                                this.requestUpdate();
                            }}
                            ?required=${required}
                            data-readonly="${readonly}"
                        >
                            <option value="">-- Bitte Wählen --</option>
                            ${field.options.map(
                                (option) => html`
                                    <option .value=${option.value.toString()} ?selected=${option.value === field.value}>
                                        ${option.label}
                                    </option>
                                `
                            )}
                        </select>
                    `;
                }
                return html`
                    <input
                        id="${fieldName}"
                        name="${fieldName}"
                        type="text"
                        .value=${field.value?.toString() || ""}
                        ?required=${required}
                        .readOnly=${readonly}
                        .pattern=${field.pattern || `.*`}
                        @input=${(e: Event) => {
                            if (field.name === "iban") {
                                const input = e.target as HTMLInputElement;
                                input.setCustomValidity(
                                    !input.value || isValidIban(input.value) ? "" : INVALID_IBAN_MESSAGE
                                );
                            }
                        }}
                        @change=${(e: Event) => {
                            const input = e.target as HTMLInputElement;
                            if (field.name === "iban") {
                                input.setCustomValidity(
                                    !input.value || isValidIban(input.value) ? "" : INVALID_IBAN_MESSAGE
                                );
                            }
                            field.value = input.value;
                            this.requestUpdate();
                        }}
                    />
                `;
        }
    }

    private _renderTableData(table: FormField, sectionIndex: number, fieldIndex: number, readonly: boolean) {
        if (!table.data) {
            return nothing;
        }

        const tableIdPart = `field_${sectionIndex}_${fieldIndex}`;
        const addRow = (e: Event) => {
            e.preventDefault();
            table.data!.push(createTableRow(table.headers!));
            this._expandedTableSections.add(`${tableIdPart}_${table.data!.length - 1}`);
            this.requestUpdate();
        };

        const removeRow = (e: Event, rowIndex: number) => {
            e.preventDefault();
            if (table.data?.length === 1) {
                table.data![0] = createTableRow(table.headers!);
            } else {
                table.data!.splice(rowIndex, 1);
            }
            this.requestUpdate();
        };

        const toggleRowVisibility = (id: string) => {
            if (this._expandedTableSections.has(id)) {
                this._expandedTableSections.delete(id);
            } else {
                this._expandedTableSections.add(id);
            }
            this.requestUpdate();
        };

        return html`<div class="padded" style="grid-column: span 2;">
            <label class="bottom-margined">
                ${table.label}
                ${table.description ? html`<div class="small subtle">${table.description}</div>` : nothing}
            </label>
            <div class="box">
                ${table.data?.map((row, rowIndex) => {
                    const dataRowId = `${tableIdPart}_${rowIndex}`;
                    const collapsed = !this._expandedTableSections.has(dataRowId);
                    return html`<div class="border-bottom">
                        <button
                            type="button"
                            class="smaller fill-horizontally transparent text-left-aligning"
                            @click=${() => toggleRowVisibility(dataRowId)}
                        >
                            <div class="horizontal spacing center-aligning layout">
                                <div class="stretch">
                                    ${row.filter((v) => v.required)?.[0]?.value || "Unvollständige Daten"}
                                </div>
                                ${collapsed
                                    ? html`<ptc-ui-icon name="icon.toggle.expand" class="smaller"></ptc-ui-icon>`
                                    : html`<ptc-ui-icon
                                          name="icon.toggle.expand-active"
                                          class="smaller"
                                      ></ptc-ui-icon>`}
                            </div>
                        </button>
                        <ptc-ui-collapse .id=${dataRowId} ?collapsed=${collapsed}>
                            <div class="padded vertical stretching layout" style="padding-bottom: 1em;">
                                ${rowIndex > 0
                                    ? html` <button
                                          type="button"
                                          class="smaller subtle red"
                                          style="margin-bottom: 1em;"
                                          @click=${(e: Event) => removeRow(e, rowIndex)}
                                      >
                                          <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon> Eintrag Entfernen
                                      </button>`
                                    : nothing}
                                <div>
                                    <div class="section-fields">
                                        ${row.map((field, fieldOfRowIndex) =>
                                            this._renderField(
                                                field,
                                                sectionIndex,
                                                fieldIndex,
                                                `${rowIndex}_${fieldOfRowIndex}`,
                                                readonly,
                                                true
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </ptc-ui-collapse>
                    </div>`;
                })}
                <div class="margined vertical layout">
                    <button
                        class="transparent smaller"
                        type="button"
                        ?disabled=${table.max !== undefined && table.data!.length >= parseInt(table.max)}
                        @click=${addRow}
                    >
                        <ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Eintrag Hinzufügen
                    </button>
                </div>
            </div>
        </div>`;
    }

    private _renderFormActions(allowEdit: boolean = true) {
        const form = this._form!;

        switch (form.status) {
            case FormStatus.Draft:
                return html`
                    <div
                        class="padded evenly stretching spacing horizontal layout form-actions"
                        ?disabled=${!allowEdit}
                    >
                        ${form.requiresEmployeeSignature
                            ? html`
                                  <button class="primary" type="button" @click=${this._shareForm}>
                                      <ptc-ui-icon name="icon.action.publish"></ptc-ui-icon> Zum Ausfüllen Freigeben
                                  </button>
                              `
                            : html`
                                  <button class="primary" type="button" @click=${this._finalizeOrShareForm}>
                                      <ptc-ui-icon name="icon.action.publish"></ptc-ui-icon> Absenden
                                  </button>
                              `}

                        <button class="transparent" type="button" @click=${this._saveForm}>
                            <ptc-ui-icon name="icon.action.save"></ptc-ui-icon> Zwischenspeichern
                        </button>
                    </div>
                `;
            case FormStatus.InProgress:
                return html`
                    <div
                        class="padded evenly stretching spacing horizontal layout form-actions"
                        ?disabled=${!allowEdit}
                    >
                        <button class="primary" type="button" @click=${this._completeForm}>
                            <ptc-ui-icon name="icon.action.publish"></ptc-ui-icon> Absenden
                        </button>

                        <button class="transparent" type="button" @click=${this._saveForm}>
                            <ptc-ui-icon name="icon.action.save"></ptc-ui-icon> Zwischenspeichern
                        </button>
                    </div>
                `;
            case FormStatus.Completed:
                return html`
                    <div
                        class="padded evenly stretching spacing horizontal layout form-actions"
                        ?disabled=${!allowEdit}
                    >
                        <button class="primary" type="button" @click=${this._finalizeForm}>
                            <ptc-ui-icon name="icon.action.finalize"></ptc-ui-icon> Fertigstellen
                        </button>
                        <button class="transparent" type="button" @click=${this._saveForm}>
                            <ptc-ui-icon name="icon.action.save"></ptc-ui-icon> Zwischenspeichern
                        </button>
                    </div>
                `;
        }
    }

    private _renderForm() {
        const form = this._form;

        if (!form) {
            return;
        }

        const isEmployee = this._document.employeeId === app.profile?.id;

        const allowEdit =
            ([FormStatus.Draft, FormStatus.Completed].includes(form.status) &&
                app.hasPermission("manage.employees.documents") &&
                app.hasPermissionForEmployee(this._employee!)) ||
            (form.status === FormStatus.InProgress && isEmployee);

        return html`
            <div class="fullbleed vertical layout">
                <ptc-scroller class="stretch">
                    <form @submit=${this._submitForm}>
                        <h1 class="center-aligning spacing horizontal layout form-title">
                            <div class="stretch">${form.name}</div>
                            <div class="small pill" style="--color-highlight: ${formStatusColor(form.status)}">
                                <ptc-ui-icon name="icon.object.form"></ptc-ui-icon>
                                ${formStatusLabel(form.status)}
                            </div>
                        </h1>
                        <div>${form.description ? html`${unsafeHTML(DOMPurify.sanitize(form.description))}` : ""}</div>
                        ${form.sections.map((section, sectionIndex) =>
                            form.checkCondition(section.condition)
                                ? html`
                                      <div class="section">
                                          ${section.title ? html`<h2>${section.title}</h2>` : ""}
                                          ${section.description
                                              ? html`<div class="small subtle section-description">
                                                    ${section.description}
                                                </div>`
                                              : ""}
                                          <div class="section-fields">
                                              ${section.fields.map((field, fieldIndex) =>
                                                  this._renderField(
                                                      field,
                                                      sectionIndex,
                                                      fieldIndex,
                                                      undefined,
                                                      !allowEdit,
                                                      field.type !== "table"
                                                  )
                                              )}
                                          </div>
                                      </div>
                                  `
                                : ""
                        )}
                        ${form.requiredConfirmations && isEmployee
                            ? html`
                                  <div class="section">
                                      <div class="section-fields">
                                          <label for="data_protection_checkbox" style="grid-column: span 2;">
                                              <div style="grid-column: span 2; margin-top: 0.5em;">
                                                  <div class="bold">
                                                      ${FORM_CONFIRMATION_TEXTS.dataProtection.title}
                                                      <span class="red colored-text">*</span>
                                                  </div>
                                                  <div class="small subtle">
                                                      ${FORM_CONFIRMATION_TEXTS.dataProtection.description}
                                                  </div>
                                              </div>
                                              <input
                                                  type="checkbox"
                                                  id="data_protection_checkbox"
                                                  name="data_protection_checkbox"
                                                  ?required=${form.status === FormStatus.InProgress}
                                                  .checked=${form.requiredConfirmations?.dataProtection?.checked ||
                                                  false}
                                                  @change=${(e: Event) =>
                                                      this._toggleRequiredConfirmation(e, "dataProtection")}
                                                  style="margin-left: 1em; margin-top: 0.5em;"
                                              />
                                              ${FORM_CONFIRMATION_TEXTS.dataProtection.confirmText}
                                          </label>

                                          <label for="id_card_checkbox" style="grid-column: span 2;">
                                              <div style="grid-column: span 2; margin-top: 0.5em;">
                                                  <div class="bold">
                                                      ${FORM_CONFIRMATION_TEXTS.idCard.title}
                                                      <span class="red colored-text">*</span>
                                                  </div>
                                                  <div class="small subtle">
                                                      ${FORM_CONFIRMATION_TEXTS.idCard.description}
                                                  </div>
                                              </div>
                                              <input
                                                  type="checkbox"
                                                  id="id_card_checkbox"
                                                  name="id_card_checkbox"
                                                  ?required=${form.status === FormStatus.InProgress}
                                                  .checked=${form.requiredConfirmations?.idCard?.checked || false}
                                                  @change=${(e: Event) => this._toggleRequiredConfirmation(e, "idCard")}
                                                  style="margin-left: 1em; margin-top: 0.5em;"
                                              />
                                              ${FORM_CONFIRMATION_TEXTS.idCard.confirmText}
                                          </label>

                                          <label for="update_information_checkbox" style="grid-column: span 2;">
                                              <div style="grid-column: span 2; margin-top: 0.5em;">
                                                  <div class="bold">
                                                      ${FORM_CONFIRMATION_TEXTS.updateInformation.title}
                                                      <span class="red colored-text">*</span>
                                                  </div>
                                                  <div class="small subtle">
                                                      ${FORM_CONFIRMATION_TEXTS.updateInformation.description}
                                                  </div>
                                              </div>
                                              <input
                                                  type="checkbox"
                                                  id="update_information_checkbox"
                                                  name="update_information_checkbox"
                                                  ?required=${form.status === FormStatus.InProgress}
                                                  .checked=${form.requiredConfirmations?.updateInformation?.checked ||
                                                  false}
                                                  @change=${(e: Event) =>
                                                      this._toggleRequiredConfirmation(e, "updateInformation")}
                                                  style="margin-left: 1em; margin-top: 0.5em;"
                                              />
                                              ${FORM_CONFIRMATION_TEXTS.updateInformation.confirmText}
                                          </label>
                                      </div>
                                  </div>
                              `
                            : ""}

                        <div class="horizontal layout signatures">
                            ${form.requiresManagerSignature && !isEmployee
                                ? html`
                                      <div
                                          class="padded click relative rounded"
                                          @click=${this._editManagerSignature}
                                          style=${isEmployee ? "pointer-events: none;" : ""}
                                      >
                                          ${form.managerSignature
                                              ? html`
                                                    <img class="signature" .src=${form.managerSignature.src} />
                                                    <div class="padded border-top">
                                                        ${form.managerSignature.name},
                                                        ${formatDate(new Date(form.managerSignature.time))}
                                                    </div>
                                                `
                                              : html`
                                                    <div class="signature"></div>
                                                    <div class="padded border-top">Unterschrift Arbeitgeber</div>
                                                `}
                                          <input
                                              ?required=${form.status === FormStatus.Completed}
                                              .value=${form.managerSignature?.src || ""}
                                              style="opacity: 0; position: absolute; bottom: 0; pointer-events: none;"
                                          />
                                      </div>
                                  `
                                : ""}

                            <div class="stretch"></div>

                            ${form.requiresEmployeeSignature
                                ? html`
                                      <div
                                          class="padded click relative rounded"
                                          @click=${this._editEmployeeSignature}
                                          style=${!isEmployee ? "pointer-events: none;" : ""}
                                      >
                                          ${form.employeeSignature
                                              ? html`
                                                    <img class="signature" .src=${form.employeeSignature.src} />
                                                    <div class="padded border-top">
                                                        ${form.employeeSignature.name},
                                                        ${formatDate(new Date(form.employeeSignature.time))}
                                                    </div>
                                                `
                                              : html`
                                                    <div class="signature"></div>
                                                    <div class="padded border-top">Unterschrift Arbeitnehmer</div>
                                                `}
                                          <input
                                              ?required=${form.status === FormStatus.InProgress}
                                              .value=${form.employeeSignature?.src || ""}
                                              style="opacity: 0; position: absolute; bottom: 0; pointer-events: none;"
                                          />
                                      </div>
                                  `
                                : ""}

                            <div class="stretch"></div>

                            ${form.requiresCaretakerSignature && isEmployee
                                ? html`
                                      <div
                                          class="padded click relative rounded"
                                          @click=${this._editCaretakerSignature}
                                          style=${!isEmployee ? "pointer-events: none;" : ""}
                                      >
                                          ${form.caretakerSignature
                                              ? html`
                                                    <img class="signature" .src=${form.caretakerSignature.src} />
                                                    <div class="padded border-top">
                                                        ${form.caretakerSignature.name},
                                                        ${formatDate(new Date(form.caretakerSignature.time))}
                                                    </div>
                                                `
                                              : html`
                                                    <div class="signature"></div>
                                                    <div class="padded border-top">
                                                        Unterschrift Gesetzlicher Vertreter
                                                    </div>
                                                `}
                                          <input
                                              ?required=${form.status === FormStatus.InProgress}
                                              .value=${form.caretakerSignature?.src || ""}
                                              style="opacity: 0; position: absolute; bottom: 0; pointer-events: none;"
                                          />
                                      </div>
                                  `
                                : ""}
                            <div class="stretch"></div>
                        </div>

                        <div class="giant spacer"></div>
                    </form>
                </ptc-scroller>

                ${this._renderFormActions(allowEdit)}
            </div>
        `;
    }

    renderContent() {
        if (!this._document) {
            return html``;
        }

        return html`
            <div class="fullbleed vertical layout background">
                <div class="horizontally-padded center-aligning horizontal layout border-bottom">
                    <ptc-ui-icon name="${fileIcon(this._document.type)}" class="large"></ptc-ui-icon>
                    <div class="padded stretch collapse">
                        <div class="bold ellipsis">${this._document.name}</div>
                        <div class="tiny pills">
                            <div class="pill">
                                <ptc-ui-icon name="icon.object.date"></ptc-ui-icon> ${formatDate(
                                    this._document.date,
                                    false
                                )}
                            </div>
                            ${this._document.hideFromEmployee
                                ? html`
                                      <div class="pill">
                                          <ptc-ui-icon name="icon.toggle.visibility-hidden"></ptc-ui-icon>
                                      </div>
                                  `
                                : ""}
                            ${this._document.tags.map(
                                (tag) =>
                                    html`<div class="pill" style="--color-highlight: ${tag.color}">
                                        <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon> ${tag.name}
                                    </div>`
                            )}
                            ${this._form
                                ? html`<div
                                      class="pill"
                                      style="--color-highlight: ${formStatusColor(this._form.status)}"
                                  >
                                      <ptc-ui-icon name="icon.object.form"></ptc-ui-icon> ${formStatusLabel(
                                          this._form.status
                                      )}
                                  </div>`
                                : ""}
                        </div>
                    </div>
                    <button class="slim transparent" @click=${this._download} ?disabled=${!this._document.url}>
                        <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                    </button>
                    <button
                        class="slim transparent"
                        @click=${this._edit}
                        ?hidden=${!this._showActions || !app.hasPermission("manage.employees.documents")}
                    >
                        <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                    </button>
                    <button
                        class="slim transparent"
                        @click=${this._delete}
                        ?hidden=${!this._showActions || !app.hasPermission("manage.employees.documents")}
                    >
                        <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                    </button>
                    <button class="slim transparent" @click=${() => this.done(this._edited)}>
                        <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                    </button>
                </div>

                <div class="stretch relative">${this._form ? this._renderForm() : this._renderPreview()}</div>
            </div>
        `;
    }
}
